import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { Icon } from '@iconify/react'
import { Input, Modal, Spin } from 'antd'
import classNames from 'classnames'

import styles from './pin-keypad-modal.module.scss'

const KEYPAD_LAYOUT = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', 'backspace'],
] as const

const KEY_PRESS_ANIMATION_MS = 160

export type PinKeypadModalClassNames = {
  wrapper?: string
  body?: string
  title?: string
  subtitle?: string
}

export type PinKeypadModalProps = {
  isVisible: boolean
  pinLength: number
  /** Sync or async verification. Return false to reject: the pad clears for another attempt. */
  onSubmit: (pin: string) => boolean | Promise<boolean>
  onClose?: () => void
  title?: string
  subtitle?: string
  dismissible?: boolean
  /** Shows a close (X) icon when dismissible. */
  closable?: boolean
  /** Allows closing by clicking the mask when dismissible. */
  maskClosable?: boolean
  /** Renders a text cancel button below the pad when dismissible. */
  cancelText?: string
  /** External loading (e.g. profile fetch) — shows a spinner instead of the pad. */
  loading?: boolean
  /** External busy (e.g. API mutation in flight) — blocks entry alongside internal submit. */
  isSubmitting?: boolean
  /** Status line shown while busy (e.g. 'Signing...'). */
  busyText?: string
  /** Caller-driven error line (wrong PIN, lockout). */
  error?: string | null
  /** Hard-disables entry (e.g. lockout window). */
  disabled?: boolean
  zIndex?: number
  width?: number
  modalClassNames?: PinKeypadModalClassNames
  /** Extra content below the pad (e.g. sign history). */
  children?: ReactNode
}

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

export const PinKeypadModal = ({
  isVisible,
  pinLength,
  onSubmit,
  onClose,
  title,
  subtitle,
  dismissible = true,
  closable = false,
  maskClosable = false,
  cancelText,
  loading = false,
  isSubmitting = false,
  busyText,
  error,
  disabled = false,
  zIndex,
  width = 560,
  modalClassNames,
  children,
}: PinKeypadModalProps) => {
  const [pin, setPin] = useState('')
  const [attemptKey, setAttemptKey] = useState(0)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [isSubmittingPin, setIsSubmittingPin] = useState(false)
  const isSubmittingPinRef = useRef(false)
  const activeKeyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isBusy = isSubmitting || isSubmittingPin
  const isEntryBlocked = disabled || isBusy || loading

  const clearActiveKeyTimeout = useCallback(() => {
    if (!activeKeyTimeoutRef.current) return
    clearTimeout(activeKeyTimeoutRef.current)
    activeKeyTimeoutRef.current = null
  }, [])

  const resetPin = useCallback(() => {
    setPin('')
    setAttemptKey((key) => key + 1)
    clearActiveKeyTimeout()
    setActiveKey(null)
  }, [clearActiveKeyTimeout])

  const animateKeyPress = useCallback(
    (key: string) => {
      clearActiveKeyTimeout()
      setActiveKey(key)
      activeKeyTimeoutRef.current = setTimeout(() => {
        setActiveKey(null)
        activeKeyTimeoutRef.current = null
      }, KEY_PRESS_ANIMATION_MS)
    },
    [clearActiveKeyTimeout],
  )

  const submitPin = useCallback(
    (pinValue: string) => {
      if (disabled || loading || isSubmitting || isSubmittingPinRef.current) return
      if (pinValue.length !== pinLength) return

      let result: boolean | Promise<boolean>
      try {
        result = onSubmit(pinValue)
      } catch {
        resetPin()
        return
      }

      // Sync verifiers resolve in-place: no busy state, the pad clears immediately on reject.
      if (typeof result === 'boolean') {
        if (!result) resetPin()
        return
      }

      isSubmittingPinRef.current = true
      setIsSubmittingPin(true)
      result
        .then((accepted) => {
          if (!accepted) resetPin()
        })
        .catch(() => resetPin())
        .finally(() => {
          isSubmittingPinRef.current = false
          setIsSubmittingPin(false)
        })
    },
    [disabled, isSubmitting, loading, onSubmit, pinLength, resetPin],
  )

  const applyPin = useCallback(
    (nextPin: string) => {
      const sanitized = onlyDigits(nextPin).slice(0, pinLength)
      setPin(sanitized)
      if (pinLength > 0 && sanitized.length === pinLength) {
        submitPin(sanitized)
      }
    },
    [pinLength, submitPin],
  )

  const handleDigitPress = useCallback(
    (digit: string) => {
      if (isEntryBlocked || isSubmittingPinRef.current) return
      if (pin.length >= pinLength) return
      animateKeyPress(digit)
      applyPin(`${pin}${digit}`)
    },
    [animateKeyPress, applyPin, isEntryBlocked, pin, pinLength],
  )

  const handleBackspace = useCallback(() => {
    if (isEntryBlocked || isSubmittingPinRef.current) return
    if (pin.length === 0) return
    animateKeyPress('backspace')
    setPin(pin.slice(0, -1))
  }, [animateKeyPress, isEntryBlocked, pin])

  const handleClose = useCallback(() => {
    if (isBusy || !dismissible) return
    resetPin()
    onClose?.()
  }, [dismissible, isBusy, onClose, resetPin])

  useEffect(() => {
    if (!isVisible) {
      resetPin()
    }
  }, [isVisible, resetPin])

  useEffect(() => {
    return () => {
      clearActiveKeyTimeout()
    }
  }, [clearActiveKeyTimeout])

  return (
    <Modal
      open={isVisible}
      onCancel={handleClose}
      closable={dismissible && closable && !isBusy}
      keyboard={dismissible && !isBusy}
      maskClosable={dismissible && maskClosable && !isBusy}
      footer={null}
      title={null}
      centered
      destroyOnHidden
      width={width}
      zIndex={zIndex}
      classNames={{
        content: styles.modalContent,
        wrapper: modalClassNames?.wrapper,
      }}>
      <div className={classNames(styles.body, modalClassNames?.body)}>
        {title && <h2 className={classNames(styles.title, modalClassNames?.title)}>{title}</h2>}
        {subtitle && (
          <p className={classNames(styles.subtitle, modalClassNames?.subtitle)}>{subtitle}</p>
        )}
        {loading ? (
          <div className={styles.loading}>
            <Spin size='large' />
          </div>
        ) : (
          pinLength > 0 && (
            <div className={styles.keypadPanel}>
              <div className={styles.otp}>
                <Input.OTP
                  key={attemptKey}
                  length={pinLength}
                  value={pin}
                  onInput={(chars) => applyPin(chars.join(''))}
                  disabled={disabled || isBusy}
                  mask='●'
                  size='large'
                  inputMode='numeric'
                  formatter={onlyDigits}
                />
              </div>
              <div className={styles.keypadGrid}>
                {KEYPAD_LAYOUT.flatMap((row, rowIndex) =>
                  row.map((key, columnIndex) => {
                    if (!key) {
                      return (
                        <span
                          key={`empty-${rowIndex}-${columnIndex}`}
                          aria-hidden='true'
                          className={styles.keySpacer}
                        />
                      )
                    }

                    const isBackspace = key === 'backspace'

                    return (
                      <button
                        key={key}
                        type='button'
                        aria-label={isBackspace ? 'Delete last PIN digit' : `PIN digit ${key}`}
                        className={classNames(styles.keypadKey, {
                          [styles.keypadKeyDanger]: isBackspace,
                          [styles.keypadKeyPressed]: activeKey === key,
                        })}
                        disabled={disabled || isBusy}
                        onClick={() => {
                          if (isBackspace) {
                            handleBackspace()
                            return
                          }
                          handleDigitPress(key)
                        }}>
                        {isBackspace ? (
                          <Icon icon='material-symbols:backspace-outline-rounded' width={24} />
                        ) : (
                          key
                        )}
                      </button>
                    )
                  }),
                )}
              </div>
              {busyText && <p className={styles.statusText}>{isBusy ? busyText : ' '}</p>}
            </div>
          )
        )}
        {error && !loading && (
          <p className={styles.error} role='alert'>
            {error}
          </p>
        )}
        {dismissible && cancelText && (
          <button type='button' className={styles.cancel} onClick={handleClose}>
            {cancelText}
          </button>
        )}
        {children}
      </div>
    </Modal>
  )
}
